import { ReactNode, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../../services/api';
import { Panel, PanelHeader, PanelBody } from '../ui/Panel';
import Button from '../ui/Button';
import Pill, { PillTone } from '../ui/Pill';
import { MiniKpiGrid } from '../ui/Kpi';
import { ErrorNotice, InfoNotice, SkeletonRows } from '../ui/States';
import { fmt } from '../../lib/format';
import { errorMessage } from '../../lib/programs';
import { Invitee } from '../../lib/events';
import { saveText, stamp } from './externalFiles';

export interface PheedloopState {
  columns: string[];
  eligible: number;
  added: number;
  changed: number;
  notAdded: number;
  lastMarkedAt: string | null;
  lastCheckinUploadAt: string | null;
}
interface FileResult {
  fileName: string;
  csv: string;
  rows: number;
  newCount: number;
  changedCount: number;
  inPerson: number;
  virtual: number;
  inviteeIds: string[];
}
type Marked = Invitee & { pheedloop?: 'added' | 'changed' | 'not_added' | null; pheedloopAddedAt?: string | null };

const STATE: Record<string, [string, PillTone]> = {
  added: ['Added', 'healthy'],
  changed: ['Changed since added', 'running'],
  not_added: ['Not added yet', 'planned'],
};

export const usePheedloopState = (eventId: string, enabled = true) =>
  useQuery<PheedloopState>(
    ['event-pheedloop', eventId],
    async () => (await api.get(`/events/${eventId}/pheedloop`)).data.data,
    { enabled },
  );

const fileKey = (eventId: string) => ['event-pheedloop-file', eventId];

/** Downloads the attendee file (new and changed, or everyone) and keeps it for "Mark as added", wherever the
 *  download started (this section's head or the Documents tab's main button). */
export function usePheedloopDownload(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    async (all: boolean) => (await api.post(`/events/${eventId}/pheedloop/file`, { all })).data.data as FileResult,
    {
      onSuccess: (d) => {
        queryClient.setQueryData(fileKey(eventId), d);
        if (d.rows) saveText(d.fileName, d.csv);
      },
    },
  );
}

// Rows stack below 768px: header hidden, each cell on its own line after its column name.
const TABLE = 'w-full border-collapse tabular max-md:block';
const HEAD = 'text-left text-small text-ink-2 border-b border-line-strong max-md:hidden';
const TH = 'py-2 pr-3 last:pr-0 align-bottom font-semibold';
const ROW = 'border-b border-line hover:bg-selected max-md:block max-md:py-3';
const TD = 'py-[11px] pr-3 last:pr-0 align-top text-[15px] leading-[22px] text-ink max-md:block max-md:p-0';
const LABELLED =
  "max-md:before:content-[attr(data-label)_'_·_'] max-md:before:text-ink-3 max-md:before:text-small max-md:before:font-normal";

function Step({ n, done, children, action }: { n: number; done: boolean; children: ReactNode; action?: ReactNode }) {
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 py-[10px] border-b border-line text-[15px] leading-[22px] text-ink">
      <span
        aria-hidden
        className={`flex-none w-6 h-6 rounded-full border text-center text-small font-semibold leading-[22px] ${
          done ? 'bg-accent border-accent text-surface' : 'border-line-strong text-ink-2'
        }`}
      >
        {done ? '✓' : n}
      </span>
      <span className="flex-[1_1_260px]">
        <span className="sr-only">{done ? 'Done: ' : `Step ${n}: `}</span>
        {children}
      </span>
      {action}
    </li>
  );
}

export default function PheedloopPanel({ eventId, canEdit }: { eventId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [showList, setShowList] = useState(false);
  const state = usePheedloopState(eventId);
  const fileQ = useQuery<FileResult | null>(fileKey(eventId), async () => null, {
    enabled: false,
    staleTime: Infinity,
    cacheTime: Infinity,
  });
  const file = fileQ.data ?? null;
  const list = useQuery<Marked[]>(
    ['event-pheedloop-list', eventId],
    async () => (await api.get(`/events/${eventId}/invitees`)).data.data.invitees,
    { enabled: canEdit && showList },
  );
  const download = usePheedloopDownload(eventId);
  const mark = useMutation(
    async () =>
      (await api.post(`/events/${eventId}/pheedloop/added`, { inviteeIds: file?.inviteeIds ?? [] })).data.data as {
        marked: number;
      },
    {
      onSuccess: () => {
        queryClient.setQueryData(fileKey(eventId), null);
        queryClient.invalidateQueries('event-pheedloop');
        queryClient.invalidateQueries('event-pheedloop-list');
      },
    },
  );
  const s = state.data;
  const rows = (list.data ?? []).filter((i) => i.pheedloop);
  const ready = Boolean(file?.rows);
  return (
    <Panel className="mt-14 max-md:mt-10">
      <PanelHeader title="PheedLoop attendees" note={s ? `last marked added ${stamp(s.lastMarkedAt)}` : undefined}>
        {canEdit ? (
          <>
            <Button aria-expanded={showList} onClick={() => setShowList((v) => !v)}>
              {showList ? 'Hide who is added' : 'Who is added'}
            </Button>
            <Button disabled={download.isLoading} onClick={() => download.mutate(true)}>
              Download everyone
            </Button>
            <Button disabled={download.isLoading} onClick={() => download.mutate(false)}>
              Download new and changed
            </Button>
          </>
        ) : null}
      </PanelHeader>
      <PanelBody>
        <p className="text-[15px] leading-[22px] text-ink-2 mb-3 max-w-[72ch]">
          Everyone registered, confirmed or attended (in person and virtual) goes into PheedLoop for the app, check-in
          and badges. The next download holds only people who are new or whose details changed.
        </p>
        {state.isLoading ? (
          <SkeletonRows rows={2} />
        ) : state.error ? (
          <ErrorNotice>Could not load PheedLoop.</ErrorNotice>
        ) : s ? (
          <div className="mt-4 mb-2">
            <MiniKpiGrid
              cells={[
                { label: 'should be in PheedLoop', value: fmt(s.eligible) },
                { label: 'added', value: fmt(s.added) },
                { label: 'not added yet', value: fmt(s.notAdded) },
                { label: 'changed since added', value: fmt(s.changed) },
              ]}
            />
          </div>
        ) : null}
        {canEdit ? (
          <>
            <ol className="mt-4 max-w-[900px]">
              <Step n={1} done={ready || mark.isSuccess}>
                Download the new and changed people.
                {file ? (
                  <span className="block text-small text-ink-3">
                    {file.rows
                      ? `${file.fileName}: ${fmt(file.rows)} people (${fmt(file.newCount)} new, ${fmt(
                          file.changedCount,
                        )} changed; ${fmt(file.inPerson)} in person, ${fmt(file.virtual)} virtual).`
                      : 'Nobody new or changed since the last file marked added.'}
                    {file.rows ? (
                      <>
                        {' '}
                        <button
                          type="button"
                          className="text-accent underline underline-offset-[3px] hover:text-ink"
                          onClick={() => saveText(file.fileName, file.csv)}
                        >
                          Download again
                        </button>
                      </>
                    ) : null}
                  </span>
                ) : null}
              </Step>
              <Step n={2} done={mark.isSuccess}>
                In PheedLoop, import the file under Attendees → Import.
              </Step>
              <Step
                n={3}
                done={mark.isSuccess}
                action={
                  <Button size="sm" disabled={!ready || mark.isLoading} onClick={() => mark.mutate()}>
                    {ready ? `Mark these ${fmt(file?.rows ?? 0)} as added` : 'Mark as added'}
                  </Button>
                }
              >
                Once PheedLoop accepts it, mark these people as added.
              </Step>
            </ol>
            {!ready && !mark.isSuccess ? (
              <p className="text-small text-ink-3 mt-2">Mark as added turns on after you download.</p>
            ) : null}
            {download.error || mark.error ? (
              <div className="mt-3">
                <ErrorNotice>
                  {errorMessage(download.error || mark.error, 'Could not prepare the PheedLoop file.')}
                </ErrorNotice>
              </div>
            ) : null}
            {mark.data ? (
              <div className="mt-3">
                <InfoNotice>{fmt(mark.data.marked)} marked as added to PheedLoop.</InfoNotice>
              </div>
            ) : null}
          </>
        ) : null}
        {s ? <p className="text-small text-ink-3 mt-3">Columns: {s.columns.join(', ')}.</p> : null}
        {canEdit && showList ? (
          <div className="mt-6">
            {list.isLoading ? (
              <SkeletonRows rows={3} />
            ) : list.error ? (
              <ErrorNotice>Could not load the list.</ErrorNotice>
            ) : rows.length ? (
              <table className={TABLE}>
                <thead className={HEAD}>
                  <tr>
                    <th className={TH}>Person</th>
                    <th className={TH}>Attending</th>
                    <th className={TH}>Status</th>
                    <th className={TH}>Added on</th>
                  </tr>
                </thead>
                <tbody className="max-md:block">
                  {rows.map((i) => (
                    <tr key={i.id} className={ROW}>
                      <td className={TD}>
                        {i.person.name}
                        {i.person.email ? <span className="block text-small text-ink-3">{i.person.email}</span> : null}
                      </td>
                      <td data-label="Attending" className={`${TD} ${LABELLED}`}>
                        {i.needs?.attendanceMode === 'virtual' ? 'Virtual' : 'In person'}
                      </td>
                      <td data-label="Status" className={`${TD} ${LABELLED}`}>
                        <Pill tone={STATE[i.pheedloop ?? 'not_added'][1]}>{STATE[i.pheedloop ?? 'not_added'][0]}</Pill>
                      </td>
                      <td
                        data-label="Added on"
                        className={`${TD} ${LABELLED} whitespace-nowrap ${i.pheedloopAddedAt ? '' : 'text-ink-3'}`}
                      >
                        {i.pheedloopAddedAt ? stamp(i.pheedloopAddedAt) : '–'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-[15px] leading-[22px] text-ink-2">Nobody registered yet.</p>
            )}
          </div>
        ) : null}
      </PanelBody>
    </Panel>
  );
}
